import { fetchWalkthroughVsWarrantyReport } from 'src/services/client';
import {
  DateRangeParams,
  OccupancyReportParams,
  OccupancyReportResponse,
  PropertyIdObject,
  SelectedValue,
  StandardReportParams,
} from 'src/pages/Reporting/ReportingFormTypes';
import moment from 'moment';

const fetchOccupancyDeficiencyReport = async (
  params: OccupancyReportParams
): Promise<OccupancyReportResponse> => {
  try {
    const response: any = await fetchWalkthroughVsWarrantyReport(
      params.listOfSelectedPropertyIds
    );

    if (!response || !response.ok) {
      throw new Error('Failed to fetch occupancy deficiency report');
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching occupancy deficiency report:', error);
    throw error;
  }
};

// Formats date values to YYYY-MM-DD strings for API
export const formatDateParams = (
  fromDate: Date | null,
  toDate: Date | null
): DateRangeParams => {
  return {
    fromDate: fromDate
      ? moment(fromDate).format('YYYY-MM-DD')
      : moment().subtract(30, 'days').format('YYYY-MM-DD'),
    toDate: toDate
      ? moment(toDate).format('YYYY-MM-DD')
      : moment().format('YYYY-MM-DD'),
  };
};

// Converts selected values to an ampersand-separated string of property IDs for the User Status Report.
export const formatPropertyIdsAsString = (
  selectedValues: SelectedValue[]
): string => {
  return selectedValues.reduce((acc: string, s: SelectedValue) => {
    if (s.id) {
      return acc ? `${acc}&${s.id}` : s.id;
    }

    return acc;
  }, '');
};

// Converts selected values to an array of property ID objects.
export const formatPropertyIdsAsArray = (
  selectedValues: SelectedValue[]
): PropertyIdObject[] => {
  return selectedValues.filter((s) => s.id).map((s) => ({ propertyId: s.id! }));
};

// Prepares report parameters based on a given report type.
const prepareReportParams = (
  reportType: string,
  selectedValues: SelectedValue[],
  fromDate: Date | null,
  toDate: Date | null
): string | StandardReportParams => {
  // For a User Status Report, return a string of property IDs.
  if (reportType === 'User Status Report') {
    return formatPropertyIdsAsString(selectedValues);
  }

  // For all other reports, return a standard params object.
  const dateParams: DateRangeParams = formatDateParams(fromDate, toDate);
  const propertyIds: PropertyIdObject[] =
    formatPropertyIdsAsArray(selectedValues);

  return {
    propertyIds,
    ...dateParams,
  };
};

export { fetchOccupancyDeficiencyReport, prepareReportParams };
